import { __awaiter, __generator } from "tslib";
import { ICON_CONSTANTS } from './icon-constants';
import { sanitizeSvgContent, fetchIconContent, getCachedIcon, sanitizeExternalType } from './icon-utils';
var IconFoundation = /** @class */ (function () {
    function IconFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._initialized = false;
        this._external = false;
        this._externalType = 'standard';
        this._lazyListener = function () { return _this._loadIcon(); };
    }
    IconFoundation.prototype.initialize = function () {
        this._adapter.initialize();
        this._applyIcon();
        this._initialized = true;
    };
    IconFoundation.prototype.disconnect = function () {
        this._adapter.destroyVisibilityObserver();
        this._clearIconQueue();
        this._initialized = false;
    };
    IconFoundation.prototype._safeApplyIcon = function () {
        if (!this._initialized) {
            return;
        }
        this._clearIconQueue();
        this._queueIconUpdate();
    };
    IconFoundation.prototype._applyIcon = function () {
        if (this._lazy && this._adapter.canLazyLoad()) {
            this._adapter.observeVisibility(this._lazyListener);
        }
        else {
            this._loadIcon();
        }
    };
    IconFoundation.prototype._clearIconQueue = function () {
        if (this._applyTimer) {
            clearTimeout(this._applyTimer);
            this._applyTimer = undefined;
        }
    };
    IconFoundation.prototype._queueIconUpdate = function () {
        var _this = this;
        this._applyTimer = window.setTimeout(function () {
            _this._applyTimer = undefined;
            _this._applyIcon();
        });
    };
    IconFoundation.prototype._loadIcon = function () {
        return __awaiter(this, void 0, void 0, function () {
            var content, url, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 6, , 7]);
                        if (!this._src) return [3 /*break*/, 1];
                        // We were provided direct icon source content so just set that
                        this._setIconContent(this._src);
                        return [3 /*break*/, 5];
                    case 1:
                        if (!this._name) return [3 /*break*/, 4];
                        content = this._tryGetIcon(this._name);
                        if (!!content) return [3 /*break*/, 3];
                        if (!this._external) {
                            // TODO: should we use a mechanism to "wait" until the icon is defined in the registery instead of ignoring it? This situtation can happen if the component is initialized before the icon is defined in the registry
                            this._adapter.setContent('');
                            return [2 /*return*/];
                        }
                        // We don't have a registry icon, so let's try the network to fetch it
                        if (!this._externalType) {
                            throw new Error("Invalid external type provided for icon: " + this._name);
                        }
                        url = this._getExternalUrl(this._name, this._externalType);
                        if (!url) return [3 /*break*/, 3];
                        return [4 /*yield*/, fetchIconContent(url, this._name)];
                    case 2:
                        content = _a.sent();
                        _a.label = 3;
                    case 3:
                        this._setIconContent(content);
                        return [3 /*break*/, 5];
                    case 4:
                        this._adapter.setContent('');
                        _a.label = 5;
                    case 5: return [3 /*break*/, 7];
                    case 6:
                        e_1 = _a.sent();
                        this._adapter.setContent('');
                        throw e_1;
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    IconFoundation.prototype._setIconContent = function (svgContent) {
        var content = sanitizeSvgContent(svgContent, this._viewbox);
        this._adapter.setContent(content);
    };
    IconFoundation.prototype._tryGetIcon = function (key) {
        return getCachedIcon(key);
    };
    IconFoundation.prototype._getExternalUrl = function (name, type) {
        if (typeof this._externalUrlBuilder === 'function') {
            return this._externalUrlBuilder(name, type);
        }
        var setName = sanitizeExternalType(this._externalType);
        return "" + ICON_CONSTANTS.strings.DEFAULT_NETWORK_BASE_URL + (setName ? "/" + setName : '') + "/" + name + ".svg";
    };
    Object.defineProperty(IconFoundation.prototype, "name", {
        get: function () {
            return this._name;
        },
        set: function (value) {
            if (this._name !== value) {
                this._name = (value || '').replace(/\s+/, '');
                this._safeApplyIcon();
                if (this._name) {
                    this._adapter.setHostAttribute(ICON_CONSTANTS.attributes.NAME, this._name);
                }
                else {
                    this._adapter.removeHostAttribute(ICON_CONSTANTS.attributes.NAME);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IconFoundation.prototype, "src", {
        get: function () {
            return this._src;
        },
        set: function (value) {
            if (this._src !== value) {
                this._src = value;
                if (this._initialized) {
                    this._applyIcon();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IconFoundation.prototype, "lazy", {
        get: function () {
            return this._lazy;
        },
        set: function (value) {
            if (this._lazy !== value) {
                this._lazy = value;
                this._safeApplyIcon();
                this._adapter.setHostAttribute(ICON_CONSTANTS.attributes.LAZY, "" + this._lazy);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IconFoundation.prototype, "external", {
        get: function () {
            return this._external;
        },
        set: function (value) {
            if (this._external !== value) {
                this._external = value;
                this._safeApplyIcon();
                this._adapter.setHostAttribute(ICON_CONSTANTS.attributes.EXTERNAL, "" + this._external);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IconFoundation.prototype, "externalType", {
        get: function () {
            return this._externalType;
        },
        set: function (value) {
            if (this._externalType !== value) {
                this._externalType = value;
                this._safeApplyIcon();
                this._adapter.setHostAttribute(ICON_CONSTANTS.attributes.EXTERNAL_TYPE, "" + this._externalType);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IconFoundation.prototype, "externalUrlBuilder", {
        set: function (cb) {
            if (this._externalUrlBuilder !== cb) {
                this._externalUrlBuilder = cb;
                this._safeApplyIcon();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IconFoundation.prototype, "viewbox", {
        get: function () {
            return this._viewbox;
        },
        set: function (value) {
            if (this._viewbox !== value) {
                this._viewbox = value;
                this._safeApplyIcon();
                this._adapter.setHostAttribute(ICON_CONSTANTS.attributes.VIEWBOX, "" + this.viewbox);
            }
        },
        enumerable: true,
        configurable: true
    });
    IconFoundation.prototype.layout = function () {
        if (this._initialized) {
            this._applyIcon();
        }
    };
    return IconFoundation;
}());
export { IconFoundation };
